import { useAccount } from "@/account/index";
import { useAppConfigContext } from "@/context/AppConfig";
import { useAppConstantsContext } from "@/context/AppConstants";
import {
  Collapse,
  Divider,
  Icons,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  styled,
} from "@/theme/index";
import { checkSingleReportVariations } from "@/utils/checkSingleReportVariations";

//TODO: clean this up this might be needed for moneybot in future after the ab test
// const ListItemButton = styled(ListItemButton)(({ theme }) => ({
//     "& :hover": {
//       backgroundColor: theme.palette.grey[200],
//       svg: {
//         backgroundColor: "transparent !important",
//       },
//     },
//     "&.Mui-selected": {
//       borderLeft: `4px solid ${theme.palette.primary.main}`,
//       backgroundColor: `${
//         theme.palette.sideNavigation?.main || theme.palette.grey[200]
//       }`,
//       "& .MuiTypography-root": {
//         fontWeight: 700,
//       },
//     },
//     "&.Mui-selected:hover": {
//       backgroundColor: `${
//         theme.palette.sideNavigation?.light || theme.palette.grey[200]
//       }`,
//     },
//     svg: {
//       backgroundColor: "transparent !important",
//     },
//   }));

//   const StyledListItemText = styled(ListItemText)(() => ({
//     backgroundColor: "transparent !important",
//     "& :hover": {
//       backgroundColor: "transparent !important",
//     },
//   }));

//   const StyledListIcon = styled(StyledListIcon)(() => ({
//     backgroundColor: "transparent !important",
//     "& :hover": {
//       backgroundColor: "transparent !important",
//     },
//   }));

export interface SideMenuItem {
  icon: JSX.Element;
  text: JSX.Element | string;
  route: string;
  name: string;
  state: string;
  "data-cy"?: string;
  childItems?: Omit<SideMenuItem, "childItems">[];
  action?: () => void;
  openInNewTab?: boolean;
  isLocked?: boolean;
}

export const StyledListIcon = styled(ListItemIcon)(() => ({
  minWidth: "36px",
}));

export function NestedComponent({
  item,
  match,
  triggerModal,
  isOpen,
  handleToggle,
  isLastItem = false,
  onNavigationClick,
}: {
  item: SideMenuItem;
  match: string;
  triggerModal?: (
    isItemLocked: boolean | undefined,
    isInSingleReportVariation: boolean,
  ) => void;
  isOpen: boolean;
  handleToggle: () => void;
  isLastItem: boolean;
  onNavigationClick?: () => void;
}) {
  const { variationPlanNames, internationalPlanNames } =
    useAppConstantsContext();

  const { data: account } = useAccount(true);

  const reportVariations =
    account && internationalPlanNames
      ? checkSingleReportVariations(
          account,
          variationPlanNames,
          internationalPlanNames,
        )
      : null;
  const isInSingleReportVariation =
    reportVariations?.isInSingleReportVariation ?? false;
  const { navigate } = useAppConfigContext().routingUtils;
  const { trackEvent } = useAppConfigContext();

  const handleOpen = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    handleToggle();
  };

  const handleOnClickSection = (
    setionItem: SideMenuItem,
    onTriggerModal?: (
      isItemLocked: boolean | undefined,
      isInSingleReportVariation: boolean,
    ) => void,
  ) => {
    if (setionItem.text === "Unclaimed Money") {
      trackEvent(`${setionItem.text}`, "navigation click", "side nav v2");
    }

    if (isInSingleReportVariation && setionItem.isLocked && onTriggerModal) {
      onTriggerModal(setionItem.isLocked, isInSingleReportVariation);
    } else {
      navigate(setionItem.route);
      onNavigationClick?.();
    }
  };
  return (
    <>
      <ListItemButton onClick={handleOpen}>
        <StyledListIcon>{item.icon}</StyledListIcon>
        <ListItemText primary={item.text} />
        {isOpen ? (
          <Icons.ExpandLess color="primary" />
        ) : (
          <Icons.ExpandMore color="primary" />
        )}
      </ListItemButton>
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {item.childItems?.map((item) => (
            <ListItemButton
              key={item.name}
              onClick={() => handleOnClickSection(item, triggerModal)}
              selected={item.route.endsWith(match)}
            >
              <StyledListIcon>{item.icon}</StyledListIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          ))}
        </List>
      </Collapse>
      {!isLastItem && (
        <Divider variant="middle" sx={{ marginY: 1, marginX: 0 }} />
      )}
    </>
  );
}

export const SingleComponent = ({
  item,
  match,
  triggerModal,
  setSearchType,
  isLastItem = false,
  onNavigationClick,
}: {
  item: SideMenuItem;
  match: string;
  triggerModal?: (
    isItemLocked: boolean | undefined,
    isInSingleReportVariation: boolean,
  ) => void;
  setSearchType?: (searchType: string) => void;
  isLastItem: boolean;
  onNavigationClick?: () => void;
}) => {
  const { navigate } = useAppConfigContext().routingUtils;
  const { trackEvent } = useAppConfigContext();

  const { data: account } = useAccount(true);
  const { variationPlanNames, internationalPlanNames } =
    useAppConstantsContext();

  const reportVariations =
    account && internationalPlanNames
      ? checkSingleReportVariations(
          account,
          variationPlanNames,
          internationalPlanNames,
        )
      : null;
  const isInSingleReportVariation =
    reportVariations?.isInSingleReportVariation ?? false;

  const handleOnClickSection = (sectionItem: SideMenuItem) => {
    if (setSearchType && typeof sectionItem.text === "string") {
      setSearchType(sectionItem.text as string);
    }
    if (
      sectionItem.text === "Unclaimed Money" ||
      sectionItem.text === "My Account"
    ) {
      trackEvent(`${sectionItem.text}`, "navigation click", "side nav");
    }

    if (isInSingleReportVariation && sectionItem.isLocked && triggerModal) {
      triggerModal(sectionItem.isLocked, isInSingleReportVariation);
    } else {
      navigate(sectionItem.route);
      onNavigationClick?.();
    }
  };
  const { name, action, route, text } = item;
  return (
    <>
      <ListItemButton
        key={name}
        onClick={() => action?.() || handleOnClickSection(item)}
        selected={route.endsWith(match)}
      >
        <StyledListIcon>{item.icon}</StyledListIcon>
        <ListItemText primary={text} />
      </ListItemButton>
      {!isLastItem && (
        <Divider variant="middle" sx={{ marginY: 1, marginX: 0 }} />
      )}
    </>
  );
};
